/**
 * Guide engine v3 - package contract (#1023, epic #1020).
 *
 * The single source of truth for the v3 package + step shapes the engine
 * consumes. Spec: docs/plans/guides/spec-v3.md (R1). Clean-room: no v1 reuse.
 *
 * Packages are JSON only, fetched from the shared guide-content CDN and
 * validated by validator.js before the engine runs them. `heading`/`body`/
 * `actionHint`/`advance[].text` render as React text (never innerHTML);
 * selectors are only ever passed to querySelector. A tampered package is inert:
 * it renders as visible text or stops the guide, it cannot execute code.
 *
 * Step fields are FLAT (no nested copy/target groups, R1.2). Sequencing is a
 * directed graph threaded by each advance condition's `next` (R1.5), not array
 * order. There is no top-level `next` and no `toc` (the agenda is deleted, R7).
 *
 * @typedef {Object} Highlight  One ringed element (R1.3).
 * @property {string} element  `[data-tour-id="..."]` selector (no class/structural CSS).
 * @property {string} color    Ring hex; unique within the step. Non-red palette (R1.3).
 * @property {string} id       Step-local id; the link target for a `click` advance.
 *
 * @typedef {Object} Advance  One completion condition (R1.4, R4). The step advances
 *   when ANY condition fires (OR). Each carries its own `next`, so a step branches by
 *   which condition fired.
 * @property {('click'|'manual'|'appears'|'value')} on  Trigger kind.
 * @property {string} next     Destination step id, or the reserved `_exit` (R1.5).
 * @property {string} [id]     `click` only: the `Highlight.id` that was clicked.
 * @property {string} [selector]  `appears`/`value` only: `[data-tour-id="..."]` of the result/field.
 * @property {string} [equals] `value` only: advance only when the value equals this (else baseline-change).
 * @property {string} [text]   `manual` only: button label (TRANSLATED). Default Next; the
 *                             `_exit`-bound manual button defaults to the Done engine string.
 *
 * @typedef {Object} Placement  Popup position, decoupled from the highlight (R1.6).
 * @property {number} x      0-100; popup left edge, percent of viewport WIDTH.
 * @property {number} y      0-100; popup top edge, percent of viewport HEIGHT.
 * @property {number} width  0-100; percent of horizontal space from `x` to viewport right edge.
 *
 * @typedef {Object} StepAudio  Narration filenames, stamped at publish (never authored).
 * @property {string} female  mp3 filename, female narration.
 * @property {string} male    mp3 filename, male narration.
 *
 * @typedef {Object} Step  Flat step (R1.2).
 * @property {string} id              Unique step id; advance `next` target + advance binding.
 * @property {string} [heading]       Popup title (TRANSLATED).
 * @property {string} body            Popup text + narration source (TRANSLATED).
 * @property {string} [actionHint]    Inline cue while gated (TRANSLATED).
 * @property {Highlight[]} [highlight] Elements to ring (R1.3). Absent = concept/observe step.
 * @property {Advance[]} advance      How the step completes + where each path goes (R1.4, R4).
 * @property {Placement} placement    Popup position (R1.6). Required on every step.
 * @property {boolean} [gate]         Opt in to deny-by-default gating (#1207): while shown, only
 *                                    the ringed target(s) + `allowExtras` + the popup are clickable;
 *                                    every other interaction is inert and its controls are dimmed
 *                                    (#1825). Default off (page stays live).
 * @property {string[]} [allowExtras] Extra `[data-tour-id="..."]` selectors left clickable while
 *                                    gated (e.g. a nav button on a page-flip step). Ignored when `gate` off.
 * @property {string[]} [topicTags]   Future AI export; not rendered.
 * @property {StepAudio} [audio]      Narration filenames (stamped at publish).
 *
 * @typedef {Object} GuidePackage  Package root (R1.1).
 * @property {string} moduleId       uuidv7; matches the catalog row PK.
 * @property {string} version        `YYYY-MM-DD.NN`; matches the catalog row.
 * @property {string} firstStepId    The entry step (the linked-list head).
 * @property {Step[]} steps          Step set keyed by `id`; sequence threaded by `next`.
 */

/** Reserved `next` value: end the guide gracefully + mark it completed (R1.5, R7). */
export const EXIT = '_exit';

/** Advance trigger kinds (R4). */
export const ADVANCE_KINDS = ['click', 'manual', 'appears', 'value'];

/** At most this many `manual` conditions (buttons) per step (R4 button cap). */
export const MAX_MANUAL_BUTTONS = 2;

/** Default locale; package file stem + fallback target (R9). */
export const DEFAULT_LOCALE = 'en-US';
